import { makeAutoObservable, runInAction } from "mobx";
import type RootStore from "stores/RootStore";
import type { ServiceStoreType } from "types/ServiceStoreTypes";
import {
  nextSort,
  sortRows,
  textValue,
  type SortDir,
  type SortValue,
} from "components/TableSort/tableSort";
import { findOrThrow, loadErrorMessage } from "shared/loadOrError";

import { isSessionEnded, sessionMark } from "stores/sessionGuard";
/**
 * FALLBACK role choices — the seven roles seeded by the API's
 * 20260530000010_recreate_roles_and_user_roles migration.
 *
 * This is no longer the canonical list. Roles are DATA: a business defines its
 * own in the `roles` table, each declaring the RBAC tier it confers, and the
 * picker reads them live via `roleChoices` below. These names are kept only so
 * the dialog still offers something sane before the roles load (or if the
 * request fails) — never widen this array to add a role, add the role.
 */
export const ROLE_CHOICES = [
  "admin",
  "controller",
  "ap_clerk",
  "ar_clerk",
  "project_manager",
  "time_keeper",
  "viewer",
] as const;

/**
 * IntraSync's OWN teams — the company that builds, sells and runs a precast
 * ERP/MES. (2026-09-29, operator.) The list this replaced was the ERP product's
 * plant departments (Casting, Curing, Yard Management…), carried over from the
 * client template: nobody at IntraSync is on "Curing".
 *
 * `users.team` is what inter-team tickets route on (`tickets.team`, the desk's
 * team picker, the Gantry feed), matched case- and space-insensitively.
 * "Engineering" MUST stay: it is the API's `FEEDBACK_TEAM` — every feedback
 * ticket an API key files goes there, and the filing is refused while no live,
 * validated staff member is on it. The old list had no Engineering, so every
 * key-filed ticket was refused. `UserManagementStore.test.ts` pins this.
 *
 * A user still carrying a team from the old list keeps it: the form offers the
 * stored value until someone chooses another.
 */
export const TEAM_CHOICES = [
  "Engineering",
  "Product",
  "Quality Assurance",
  "DevOps & Infrastructure",
  "Data & Integrations",
  "Implementation",
  "Precast Consulting",
  "Training",
  "Customer Support",
  "Customer Success",
  "Sales",
  "Marketing",
  "Partnerships",
  "Finance & Accounting",
  "Operations",
  "Leadership",
] as const;

/**
 * There is deliberately NO fallback list of department names. The picker used
 * to offer HQ's team names (TEAM_CHOICES) while the `departments` table was
 * loading, failed to load, or held no active row — and an admin could save one
 * as `users.department`: a department string with no `departments` row behind
 * it. The picker's only source is Administration → Departments
 * (`departmentPicker` below); with nothing there it says so and links to the
 * page that creates one. An existing user's stored value stays selectable.
 */

/** Where the `departments` lookup stands (the picker is usable only when `loaded`). */
export type DepartmentsStatus = "idle" | "loading" | "loaded" | "error";

/** What the department picker shows and whether it can be used. */
export interface DepartmentPicker {
  /** `ready` is the only state in which a department can be chosen. */
  state: "loading" | "error" | "empty" | "ready";
  /** Choices: the active departments, plus the form's current value. */
  choices: string[];
  /** The lookup failure for `error`, otherwise null. */
  message: string | null;
}

/**
 * Whether the department select accepts a change. `ready`: yes. `empty` (the
 * Departments table has no active department): yes — but only when the account
 * carries a stale value to clear, so the choices are "None" and that value;
 * `isDepartmentSavable` accepts blank, and without this an admin could not
 * remove a department that no longer exists. `loading`/`error`: no — the
 * account keeps what it has until the lookup answers.
 */
export function isDepartmentPickerEditable(picker: DepartmentPicker): boolean {
  if (picker.state === "ready") return true;
  return picker.state === "empty" && picker.choices.length > 0;
}

/** One user row as this view reads it from the builtin `userStore` cache. */
export interface UserRow {
  id: number | string;
  email?: string;
  firstName?: string;
  lastName?: string;
  role?: string;
  department?: string;
  team?: string;
  isValidated?: boolean;
  adminUser?: boolean;
  /**
   * Set when a Microsoft sign-up filed this account for approval (the API's
   * `users.awaitingApprovalSince`): it is inactive, linked to the person's
   * Microsoft account, and nobody has decided about it yet. Read-only; the
   * API clears it when the account is activated.
   */
  awaitingApprovalSince?: string | null;
  /** The Entra object id the account signs in with Microsoft as, once linked. */
  microsoftId?: string | null;
}

/**
 * An account a Microsoft sign-up filed that no administrator has activated
 * yet. An active account is never "waiting", whatever the column says.
 */
export const isWaitingForApproval = (user: Pick<UserRow, "isValidated" | "awaitingApprovalSince">): boolean =>
  !user.isValidated && !!user.awaitingApprovalSince;

/**
 * The banner over the list while Microsoft sign-ups wait. Says what an
 * administrator does next, and that nothing is open until they do.
 */
export const waitingSentence = (count: number): string =>
  `${count === 1 ? "1 person has" : `${count} people have`} signed in with Microsoft and ${
    count === 1 ? "is" : "are"
  } waiting for HQ access. Choose Approve and activate in each one's menu and pick their role. Until then they cannot sign in.`;

/** Whether the account has signed in with Microsoft (its identity is linked). */
export const signsInWithMicrosoft = (user: Pick<UserRow, "microsoftId">): boolean =>
  typeof user.microsoftId === "string" && user.microsoftId.length > 0;

export type StatusFilter = "all" | "active" | "inactive" | "waiting" | "admins";

export type UserSortKey = "name" | "role" | "department" | "team" | "status";

/** Every user column is text, so every column starts A→Z. */
const DESC_FIRST: ReadonlySet<UserSortKey> = new Set<UserSortKey>();

/** The word the Status column's primary chip shows — also its sort value. */
export const statusLabel = (user: UserRow): string =>
  user.adminUser
    ? "Admin"
    : user.isValidated
      ? "Active"
      : isWaitingForApproval(user)
        ? "Waiting for approval"
        : "Inactive";

/**
 * The role chip a user's status chips carry, or null — so the role is shown
 * ONCE (walkthrough: "Admin" chip, then an "admin" role chip, beside a Role
 * column that already said it). The table has a Role column, so its chips
 * carry no role; a phone card has none, so its chips do — except when the
 * role is "admin" and the Admin chip is already there.
 */
export const roleChipLabel = (
  user: Pick<UserRow, "role" | "adminUser">,
  roleShownElsewhere: boolean,
): string | null => {
  const role = user.role?.trim();
  if (!role || roleShownElsewhere) return null;
  if (user.adminUser && role.toLowerCase() === "admin") return null;
  return role;
};

/** The subset of the raw Feathers `user` service this view writes through. */
interface UserApi {
  create(data: object): Promise<unknown>;
  patch(id: number | string, data: object): Promise<unknown>;
  remove(id: number | string): Promise<unknown>;
}

export interface UserFormData {
  email: string;
  firstName: string;
  lastName: string;
  password: string;
  confirmPassword: string;
  role: string;
  department: string;
  team: string;
  isValidated: boolean;
  adminUser: boolean;
}

const emptyForm = (): UserFormData => ({
  email: "",
  firstName: "",
  lastName: "",
  password: "",
  confirmPassword: "",
  role: "viewer",
  department: "",
  team: "",
  // Admin-created accounts are validated by default so the person can log in
  // immediately with the initial password handed to them.
  isValidated: true,
  adminUser: false,
});

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Display label for a user row — full name, falling back to the email local part. */
export const userDisplayName = (user: UserRow): string => {
  const name = [user.firstName, user.lastName].filter(Boolean).join(" ").trim();
  if (name) return name;
  return String(user.email ?? "").split("@")[0] || "Unnamed user";
};

const UPPER = "ABCDEFGHJKLMNPQRSTUVWXYZ";
const LOWER = "abcdefghijkmnopqrstuvwxyz";
const DIGITS = "23456789";
const SYMBOLS = "!@#$%^&*?-_";

/** Cryptographically random index into `set`. */
const pick = (set: string): string => {
  const buf = new Uint32Array(1);
  crypto.getRandomValues(buf);
  return set[buf[0] % set.length];
};

/**
 * Generate a temporary password that satisfies any reasonable configuration of
 * the server-side policy (min length, upper, lower, number, symbol are all the
 * knobs `validatePasswordAgainstPolicy` checks). Ambiguous glyphs (I/l/1, O/0)
 * are excluded because these get read aloud or typed off a sticky note.
 */
export const generateTemporaryPassword = (length = 16): string => {
  const required = [pick(UPPER), pick(LOWER), pick(DIGITS), pick(SYMBOLS)];
  const all = UPPER + LOWER + DIGITS + SYMBOLS;
  const rest = Array.from({ length: Math.max(0, length - required.length) }, () =>
    pick(all)
  );
  const chars = [...required, ...rest];
  // Fisher-Yates so the guaranteed classes aren't always in the first 4 slots.
  for (let i = chars.length - 1; i > 0; i -= 1) {
    const buf = new Uint32Array(1);
    crypto.getRandomValues(buf);
    const j = buf[0] % (i + 1);
    [chars[i], chars[j]] = [chars[j], chars[i]];
  }
  return chars.join("");
};

/** Pull a Feathers BadRequest's per-rule details (password policy) into a list. */
const errorDetails = (err: unknown): string[] => {
  const data = (err as { data?: { errors?: unknown } })?.data;
  return Array.isArray(data?.errors) ? data.errors.map((e) => String(e)) : [];
};

const errorMessage = (err: unknown, fallback: string): string =>
  err instanceof Error && err.message ? err.message : fallback;

/**
 * Route ViewStore for the User Management page.
 *
 * Owns the page's filter state, the create/edit form and the password-reset
 * form. The user records themselves stay in the builtin `userStore`
 * ServiceStore — per the MobX rule this store only derives over them.
 *
 * Password reset needs no dedicated API surface: a PATCH to the `user` service
 * with `{ password }` runs validatePasswordPolicy (policy + reuse check),
 * hashes via userPatchResolver, records password_history and writes an audit
 * row — and restrictUserMutations already lets an admin patch any account.
 */
class UserManagementStore {
  rootStore: RootStore;

  // ---- Page filters ----
  search = "";
  statusFilter: StatusFilter = "all";

  // Column sort (applied client-side over the filtered rows)
  sortKey: UserSortKey = "name";
  sortDir: SortDir = "asc";

  // ---- Create/edit form ----
  /** Null while creating; the id of the account being edited otherwise. */
  editingUserId: number | string | null = null;
  formData: UserFormData = emptyForm();
  saving = false;
  /** Top-line API/validation failure shown in the dialog. */
  formError: string | null = null;
  /** Per-rule details (e.g. password-policy violations) from the API error. */
  formErrorDetails: string[] = [];

  // ---- Password reset ----
  resetUserId: number | string | null = null;
  resetPasswordValue = "";
  resetConfirmValue = "";
  /** Reveals the generated password so an admin can read it back. */
  showResetPassword = false;
  resetting = false;
  resetError: string | null = null;
  resetErrorDetails: string[] = [];

  // ---- Hard delete ----
  removingUserId: number | string | null = null;
  /** The deliberate second action gating the Remove button. */
  removeAcknowledged = false;
  removing = false;
  removeError: string | null = null;
  removeErrorDetails: string[] = [];

  /** Id of the row whose inline toggle (admin/active) is in flight. */
  rowBusyId: number | string | null = null;
  rowError: string | null = null;

  /**
   * Why the user list could not be loaded, or null after a successful load.
   * The `userStore` ServiceStore swallows a failed `find` (resolves null), so
   * without this a failed load read as "No user accounts yet".
   */
  loadError: string | null = null;

  /**
   * The department lookup, tracked on its own: loading, loaded and failed are
   * three different things the picker must not blur into "no departments".
   */
  departmentsStatus: DepartmentsStatus = "idle";
  departmentsError: string | null = null;
  /** The department the account being edited had when the dialog opened ("" when creating). */
  private departmentOnOpen = "";

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  /** The builtin `user` ServiceStore (mounted by RootStore) — reads + cache. */
  private get userService(): ServiceStoreType | null {
    return (this.rootStore.userStore as ServiceStoreType | null) ?? null;
  }

  /**
   * The raw Feathers `user` service — every WRITE goes here, not through the
   * ServiceStore.
   *
   * RootStore mounts `userStore` as a base ServiceStore, whose create/patch/
   * remove SWALLOW server errors: they record `errorOnCreate`/`errorOnPatch`/
   * `errorOnRemove` and resolve `undefined` rather than rejecting. Writing
   * through it would report a rejected password policy, a refused last-admin
   * delete or a Forbidden as SUCCESS. The raw service rejects properly, so the
   * API's message reaches the admin; the cache is refreshed with find()
   * afterwards.
   */
  private get userApi(): UserApi | null {
    const app = this.rootStore.app as
      | { service?: (path: string) => UserApi }
      | undefined;
    return app?.service?.("user") ?? null;
  }

  /** The `roles` ServiceStore (configured in serviceStores/user.stores.ts). */
  private get rolesService(): ServiceStoreType | null {
    return (
      (this.rootStore as unknown as { rolesStore?: ServiceStoreType })
        .rolesStore ?? null
    );
  }

  /**
   * Role names for the Add/Edit User picker, newest source of truth first:
   * whatever the `roles` table actually holds, falling back to the seeded seven
   * only while the list is empty (not yet loaded, or the request failed).
   *
   * Sorted with the built-in roles first so the familiar names stay at the top
   * of a list a business may have added a dozen of its own to.
   */
  get roleChoices(): string[] {
    const rows = (this.rolesService?.entities ?? []) as Array<{
      name?: unknown;
      isSystem?: unknown;
    }>;

    const names = rows
      .map((r) => (typeof r.name === "string" ? r.name.trim() : ""))
      .filter((n) => n.length > 0);

    if (names.length === 0) return [...ROLE_CHOICES];

    const isBuiltin = (n: string) =>
      rows.some((r) => r.name === n && r.isSystem === true);

    return [...new Set(names)].sort((a, b) => {
      const rank = Number(isBuiltin(b)) - Number(isBuiltin(a));
      return rank !== 0 ? rank : a.localeCompare(b);
    });
  }

  /** The `departments` ServiceStore (serviceStores/administration.stores.ts). */
  private get departmentsService(): ServiceStoreType | null {
    return (
      (this.rootStore as unknown as { departmentsStore?: ServiceStoreType })
        .departmentsStore ?? null
    );
  }

  /**
   * Load the departments the picker offers. Called from the route's entry (the
   * header Refresh re-runs it) and the dialog's Retry. Goes through
   * `findOrThrow`: the ServiceStore swallows a failed find, which used to leave
   * the picker looking merely empty.
   */
  async loadDepartments(): Promise<void> {
    const service = this.departmentsService;
    if (!service) {
      runInAction(() => {
        this.departmentsStatus = "error";
        this.departmentsError = "The departments service is not available in this build.";
      });
      return;
    }
    const ended = sessionMark(this.rootStore);
    runInAction(() => {
      this.departmentsStatus = "loading";
      this.departmentsError = null;
    });
    try {
      await findOrThrow(service, { query: { $limit: 500 } });
      if (ended()) return;
      runInAction(() => {
        this.departmentsStatus = "loaded";
      });
    } catch (err) {
      if (ended()) return;
      runInAction(() => {
        this.departmentsStatus = "error";
        this.departmentsError = loadErrorMessage(err, "The departments could not be loaded.");
      });
    }
  }

  /**
   * The Add/Edit User department picker: `ready` only once the lookup has
   * loaded AND holds an active department; loading and failed are their own
   * states, and an empty table says so instead of offering made-up names.
   *
   * The department the account had when the dialog OPENED stays among the
   * choices for as long as the dialog is open, even when it is not a live
   * department (an inactive or deleted one, or a pre-pivot plant name): editing
   * someone never silently clears it, and an admin who picks "None" or another
   * department can still pick it back. It used to follow the form's CURRENT
   * value instead, so the first change dropped it from the list — and with no
   * live departments, emptied the choices and disabled the select mid-edit.
   * The current value is kept too (it can only differ by being live).
   */
  get departmentPicker(): DepartmentPicker {
    const live = this.liveDepartmentNames;
    const isLive = (name: string) =>
      live.some((d) => d.toLowerCase() === name.toLowerCase());
    const extras: string[] = [];
    for (const name of [this.departmentOnOpen.trim(), this.formData.department.trim()]) {
      if (!name || isLive(name)) continue;
      if (extras.some((e) => e.toLowerCase() === name.toLowerCase())) continue;
      extras.push(name);
    }
    const choices = extras.length ? [...extras, ...live] : live;
    if (this.departmentsStatus === "error") {
      return { state: "error", choices, message: this.departmentsError };
    }
    if (this.departmentsStatus !== "loaded") {
      return { state: "loading", choices, message: null };
    }
    return { state: live.length === 0 ? "empty" : "ready", choices, message: null };
  }

  /** The picker's choices (see {@link departmentPicker}). */
  get departmentChoices(): string[] {
    return this.departmentPicker.choices;
  }

  /**
   * Close the dialog and go to Administration → Departments, where a
   * department is created.
   */
  openDepartmentsPage() {
    this.rootStore.routerStore?.closeDialog?.();
    void this.rootStore.routerStore?.goTo("Departments");
  }

  /**
   * True when `department` may be saved: blank, unchanged from what the
   * account already had, or the name of an ACTIVE department. Anything else
   * would write a department string with no `departments` row behind it.
   */
  private isDepartmentSavable(department: string): boolean {
    const wanted = department.trim().toLowerCase();
    if (!wanted) return true;
    if (wanted === this.departmentOnOpen.trim().toLowerCase()) return true;
    return this.liveDepartmentNames.some((d) => d.toLowerCase() === wanted);
  }

  /** Active department names from the service, de-duplicated, A→Z. */
  private get liveDepartmentNames(): string[] {
    const rows = (this.departmentsService?.entities ?? []) as Array<{
      name?: unknown;
      isActive?: unknown;
    }>;
    const names = rows
      .filter((r) => r.isActive !== false)
      .map((r) => (typeof r.name === "string" ? r.name.trim() : ""))
      .filter((n) => n.length > 0);
    return [...new Set(names)].sort((a, b) => a.localeCompare(b));
  }

  /**
   * Load the role list for the picker. Deliberately swallows failure: an admin
   * must still be able to edit a user's name or department when the roles
   * request is down, and the picker degrades to ROLE_CHOICES.
   */
  async loadRoles(): Promise<void> {
    try {
      await this.rolesService?.find?.({ query: { $limit: 200 } });
    } catch {
      // Picker falls back to ROLE_CHOICES.
    }
  }

  /** Raises when the API client is not available, rather than failing silently. */
  private requireApi(): UserApi {
    const api = this.userApi;
    if (!api) {
      throw new Error("Not connected to the API — reload and try again.");
    }
    return api;
  }

  // ---- Filters & derived list ----

  setSearch(value: string) {
    this.search = value;
  }

  setStatusFilter(value: StatusFilter) {
    this.statusFilter = value;
  }

  /** Column-header click: same column toggles direction, a new one starts A→Z. */
  setSort(key: UserSortKey) {
    const next = nextSort(this.sortKey, this.sortDir, key, DESC_FIRST);
    this.sortKey = next.key;
    this.sortDir = next.dir;
  }

  get loading(): boolean {
    return this.userService?.isFindPending === true;
  }

  /** Every cached user, sorted by display name. */
  get allUsers(): UserRow[] {
    const entities = (this.userService?.entities ?? []) as unknown as UserRow[];
    return entities
      .filter((user) => user?.id != null)
      .slice()
      .sort((a, b) => userDisplayName(a).localeCompare(userDisplayName(b)));
  }

  /** The rows the page renders — `allUsers` narrowed by search + status. */
  get users(): UserRow[] {
    const term = this.search.trim().toLowerCase();
    return this.allUsers.filter((user) => {
      switch (this.statusFilter) {
        case "active":
          if (!user.isValidated) return false;
          break;
        case "inactive":
          if (user.isValidated) return false;
          break;
        case "waiting":
          if (!isWaitingForApproval(user)) return false;
          break;
        case "admins":
          if (!user.adminUser) return false;
          break;
        default:
          break;
      }
      if (!term) return true;
      return [
        user.email,
        user.firstName,
        user.lastName,
        user.role,
        user.department,
        user.team,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(term);
    });
  }

  /**
   * The rows both layouts render: `users` (search + status narrowed) under the
   * active column sort. Missing values (no department/team) sort last in
   * either direction.
   */
  get sortedUsers(): UserRow[] {
    const key = this.sortKey;
    const valueOf = (user: UserRow): SortValue => {
      switch (key) {
        case "name":
          return textValue(userDisplayName(user));
        case "role":
          return textValue(user.role);
        case "department":
          return textValue(user.department);
        case "team":
          return textValue(user.team);
        case "status":
          return statusLabel(user);
      }
    };
    return sortRows(this.users, valueOf, this.sortDir);
  }

  get adminCount(): number {
    return this.allUsers.filter((user) => user.adminUser).length;
  }

  get inactiveCount(): number {
    return this.allUsers.filter((user) => !user.isValidated).length;
  }

  /** Accounts a Microsoft sign-up filed that are waiting for an administrator. */
  get waitingCount(): number {
    return this.allUsers.filter(isWaitingForApproval).length;
  }

  findUser(id: number | string | null): UserRow | null {
    if (id == null) return null;
    return this.allUsers.find((user) => String(user.id) === String(id)) ?? null;
  }

  /**
   * Load (or reload) the user list. Never rejects: a failure lands on
   * `loadError`, which the body renders as an error with Retry.
   */
  async load(): Promise<void> {
    try {
      await findOrThrow(this.userService);
      runInAction(() => {
        this.loadError = null;
      });
    } catch (err: unknown) {
      runInAction(() => {
        if (!isSessionEnded(err)) this.loadError = loadErrorMessage(err, "The user accounts could not be loaded.");
      });
    }
  }

  async refresh(): Promise<void> {
    await this.load();
  }

  // ---- Create / edit form ----

  get isEditing(): boolean {
    return this.editingUserId != null;
  }

  get editingUser(): UserRow | null {
    return this.findUser(this.editingUserId);
  }

  startCreate() {
    this.editingUserId = null;
    this.departmentOnOpen = "";
    this.formData = emptyForm();
    this.formError = null;
    this.formErrorDetails = [];
  }

  /**
   * Seed the form from an existing account. Password fields stay empty and are
   * never submitted from this dialog — a password change goes through the
   * dedicated reset flow so it can be confirmed and read back deliberately.
   */
  startEdit(user: UserRow) {
    this.editingUserId = user.id;
    this.departmentOnOpen = user.department ?? "";
    this.formData = {
      email: user.email ?? "",
      firstName: user.firstName ?? "",
      lastName: user.lastName ?? "",
      password: "",
      confirmPassword: "",
      // An account a Microsoft sign-up filed has no role CHOSEN: its row holds
      // the column's 'viewer' default only because the column needs a value.
      // So the picker starts empty and the approval has to choose one (the API
      // refuses an activation that names none — approveWaitingAccount.ts).
      role: isWaitingForApproval(user) ? "" : (user.role ?? "viewer"),
      department: user.department ?? "",
      team: user.team ?? "",
      isValidated: user.isValidated ?? false,
      adminUser: user.adminUser ?? false,
    };
    this.formError = null;
    this.formErrorDetails = [];
  }

  /**
   * Approve an account a Microsoft sign-up filed: the edit dialog, with Active
   * already on and the role still to choose. Approving is never a bare
   * activation, which would leave the person with the column's default role.
   */
  startApprove(user: UserRow) {
    this.startEdit(user);
    this.formData = { ...this.formData, isValidated: true };
  }

  /** Saving would activate a waiting account without a role (the API refuses it too). */
  get approvalNeedsRole(): boolean {
    return this.editingWaitsForApproval && this.formData.isValidated && !this.formData.role.trim();
  }

  setFormField(field: keyof UserFormData, value: string | boolean) {
    this.formData = { ...this.formData, [field]: value } as UserFormData;
  }

  get isEmailValid(): boolean {
    return EMAIL_PATTERN.test(this.formData.email.trim());
  }

  /** The account open in the edit dialog is one a Microsoft sign-up filed for approval. */
  get editingWaitsForApproval(): boolean {
    const user = this.isEditing ? this.findUser(this.editingUserId) : null;
    return !!user && isWaitingForApproval(user);
  }

  get passwordsMatch(): boolean {
    return this.formData.password === this.formData.confirmPassword;
  }

  get isFormValid(): boolean {
    const identityValid =
      this.isEmailValid &&
      !!this.formData.firstName.trim() &&
      !!this.formData.lastName.trim();
    if (!identityValid) return false;
    if (this.approvalNeedsRole) return false;
    // An initial password is required only when creating the account.
    if (this.isEditing) return true;
    return this.formData.password.length > 0 && this.passwordsMatch;
  }

  /**
   * Create the account, or patch it when editing (both admin-only on the API —
   * see restrictUserMutations). Email uniqueness and password policy are
   * enforced server-side; failures land in `formError` / `formErrorDetails` and
   * the form state is preserved so the dialog can be re-opened for correction.
   */
  async saveUser(): Promise<boolean> {
    if (!this.isFormValid) {
      this.formError = this.approvalNeedsRole
        ? "Choose a role before activating this account. It was created by a Microsoft sign-in, and no role has been chosen for it yet."
        : !this.isEditing && !this.passwordsMatch
          ? "Passwords do not match."
          : "Email, first name and last name are required.";
      this.formErrorDetails = [];
      return false;
    }
    if (!this.isDepartmentSavable(this.formData.department)) {
      this.formError =
        "Choose a department from Administration → Departments. That name is not one of them.";
      this.formErrorDetails = [];
      return false;
    }
    this.saving = true;
    this.formError = null;
    this.formErrorDetails = [];
    const editingId = this.editingUserId;
    try {
      const payload: Record<string, unknown> = {
        email: this.formData.email.trim().toLowerCase(),
        firstName: this.formData.firstName.trim(),
        lastName: this.formData.lastName.trim(),
        // A waiting account saved without a role chosen yet keeps the role it
        // has; an empty string would be written over it.
        ...(this.formData.role.trim() ? { role: this.formData.role } : {}),
        isValidated: this.formData.isValidated,
        adminUser: this.formData.adminUser,
        // Clearing the select must clear the stored value, so send an empty
        // string on edit rather than omitting the key.
        ...(this.formData.department || editingId != null
          ? { department: this.formData.department }
          : {}),
        ...(this.formData.team || editingId != null
          ? { team: this.formData.team }
          : {}),
      };

      const api = this.requireApi();
      if (editingId != null) {
        await api.patch(editingId, payload);
      } else {
        payload.password = this.formData.password;
        await api.create(payload);
      }

      // Refresh the list so the change appears without a manual reload.
      await this.userService?.find();
      runInAction(() => {
        this.formData = emptyForm();
        this.editingUserId = null;
      });
      return true;
    } catch (err: unknown) {
      runInAction(() => {
        if (!isSessionEnded(err)) {
          this.formError = errorMessage(
            err,
            editingId != null ? "Failed to update user" : "Failed to create user"
          );
        }
        if (!isSessionEnded(err)) this.formErrorDetails = errorDetails(err);
      });
      console.error("saveUser failed:", err);
      return false;
    } finally {
      runInAction(() => {
        this.saving = false;
      });
    }
  }

  // ---- Password reset ----

  get resetUser(): UserRow | null {
    return this.findUser(this.resetUserId);
  }

  startPasswordReset(user: UserRow) {
    this.resetUserId = user.id;
    this.resetPasswordValue = "";
    this.resetConfirmValue = "";
    this.showResetPassword = false;
    this.resetError = null;
    this.resetErrorDetails = [];
  }

  setResetPassword(value: string) {
    this.resetPasswordValue = value;
  }

  setResetConfirm(value: string) {
    this.resetConfirmValue = value;
  }

  setShowResetPassword(value: boolean) {
    this.showResetPassword = value;
  }

  /**
   * Fill both fields with a generated temporary password and reveal it — the
   * admin has to be able to read it back to hand it over.
   */
  generateResetPassword() {
    const generated = generateTemporaryPassword();
    this.resetPasswordValue = generated;
    this.resetConfirmValue = generated;
    this.showResetPassword = true;
  }

  get resetPasswordsMatch(): boolean {
    return this.resetPasswordValue === this.resetConfirmValue;
  }

  get isResetValid(): boolean {
    return (
      this.resetUserId != null &&
      this.resetPasswordValue.length > 0 &&
      this.resetPasswordsMatch &&
      !this.resetting
    );
  }

  /**
   * Set a new password on the target account. The API's patch pipeline does the
   * real work: policy validation, reuse rejection against password_history,
   * bcrypt hashing and the audit row.
   */
  async submitPasswordReset(): Promise<boolean> {
    if (this.resetUserId == null) {
      this.resetError = "No user selected.";
      return false;
    }
    if (!this.resetPasswordValue) {
      this.resetError = "Enter a new password.";
      this.resetErrorDetails = [];
      return false;
    }
    if (!this.resetPasswordsMatch) {
      this.resetError = "Passwords do not match.";
      this.resetErrorDetails = [];
      return false;
    }
    this.resetting = true;
    this.resetError = null;
    this.resetErrorDetails = [];
    try {
      await this.requireApi().patch(this.resetUserId, {
        password: this.resetPasswordValue,
      });
      runInAction(() => {
        this.resetPasswordValue = "";
        this.resetConfirmValue = "";
        this.showResetPassword = false;
        this.resetUserId = null;
      });
      return true;
    } catch (err: unknown) {
      runInAction(() => {
        if (!isSessionEnded(err)) this.resetError = errorMessage(err, "Failed to reset password");
        if (!isSessionEnded(err)) this.resetErrorDetails = errorDetails(err);
      });
      console.error("submitPasswordReset failed:", err);
      return false;
    } finally {
      runInAction(() => {
        this.resetting = false;
      });
    }
  }

  // ---- Hard delete ----

  get removingUser(): UserRow | null {
    return this.findUser(this.removingUserId);
  }

  /**
   * A hard delete must never be queued. OfflineServiceStore.remove records a
   * soft-delete in the outbox while offline and resolves successfully, which
   * would tell the admin the account is gone when nothing has happened yet.
   */
  get isOffline(): boolean {
    return this.rootStore.routerStore?.isEffectivelyOffline === true;
  }

  startRemove(user: UserRow) {
    this.removingUserId = user.id;
    this.removeAcknowledged = false;
    this.removeError = null;
    this.removeErrorDetails = [];
  }

  setRemoveAcknowledged(value: boolean) {
    this.removeAcknowledged = value;
  }

  get isRemoveValid(): boolean {
    return (
      this.removingUserId != null &&
      this.removeAcknowledged &&
      !this.removing &&
      !this.isOffline
    );
  }

  /**
   * Permanently delete the account. The API does the hard part: guardUserRemoval
   * refuses self-deletion and the last usable admin, detachUserReferences clears
   * the 32 un-cascaded foreign keys inside the delete transaction, and anything
   * it misses comes back as a readable Conflict rather than a raw SQL 500.
   */
  async removeUser(): Promise<boolean> {
    if (this.removingUserId == null) {
      this.removeError = "No user selected.";
      return false;
    }
    if (this.isOffline) {
      this.removeError =
        "You are offline — removing an account cannot be queued. Reconnect and try again.";
      return false;
    }
    this.removing = true;
    this.removeError = null;
    this.removeErrorDetails = [];
    try {
      await this.requireApi().remove(this.removingUserId);
      await this.userService?.find();
      runInAction(() => {
        this.removingUserId = null;
        this.removeAcknowledged = false;
      });
      return true;
    } catch (err: unknown) {
      runInAction(() => {
        if (!isSessionEnded(err)) this.removeError = errorMessage(err, "Failed to remove user");
        if (!isSessionEnded(err)) this.removeErrorDetails = errorDetails(err);
      });
      console.error("removeUser failed:", err);
      return false;
    } finally {
      runInAction(() => {
        this.removing = false;
      });
    }
  }

  // ---- Inline row actions ----

  private async patchRow(
    user: UserRow,
    data: Record<string, unknown>,
    failureMessage: string
  ): Promise<boolean> {
    this.rowBusyId = user.id;
    this.rowError = null;
    try {
      await this.requireApi().patch(user.id, data);
      // The ServiceStore cache is not updated by a raw write, so refresh it —
      // otherwise the row would keep rendering its stale admin/active state.
      await this.userService?.find();
      return true;
    } catch (err: unknown) {
      runInAction(() => {
        if (!isSessionEnded(err)) this.rowError = errorMessage(err, failureMessage);
      });
      console.error(`${failureMessage}:`, err);
      return false;
    } finally {
      runInAction(() => {
        this.rowBusyId = null;
      });
    }
  }

  /** Flip `isValidated` — the flag that decides whether the account can log in. */
  toggleValidated(user: UserRow): Promise<boolean> {
    return this.patchRow(
      user,
      { isValidated: !user.isValidated },
      "Failed to change account status"
    );
  }

  /** Grant or revoke the admin flag. */
  toggleAdmin(user: UserRow): Promise<boolean> {
    return this.patchRow(
      user,
      { adminUser: !user.adminUser },
      "Failed to change administrator access"
    );
  }

  clearRowError() {
    this.rowError = null;
  }

  /**
   * Back to the constructed state, so nothing of the person who signed out
   * is left for the next one. Called by RootStore.purgeUserData on sign-out.
   */
  reset() {
    this.search = "";
    this.statusFilter = "all";
    this.sortKey = "name";
    this.sortDir = "asc";
    this.editingUserId = null;
    this.departmentOnOpen = "";
    this.departmentsStatus = "idle";
    this.departmentsError = null;
    this.formData = emptyForm();
    this.saving = false;
    this.formError = null;
    this.formErrorDetails = [];
    this.resetUserId = null;
    this.resetPasswordValue = "";
    this.resetConfirmValue = "";
    this.showResetPassword = false;
    this.resetting = false;
    this.resetError = null;
    this.resetErrorDetails = [];
    this.removingUserId = null;
    this.removeAcknowledged = false;
    this.removing = false;
    this.removeError = null;
    this.removeErrorDetails = [];
    this.rowBusyId = null;
    this.rowError = null;
    this.loadError = null;
  }
}

export default UserManagementStore;
